import React, {useMemo} from 'react';
import {useDropzone} from 'react-dropzone';
import { Grid, Alert, Space, Tag } from '@arco-design/web-react';

const { Row, Col } = Grid;

// MUI -> Arco. MUI Grid container/item -> Arco Grid Row/Col (MUI /12 -> Arco
// /24, so xs={12} becomes span={24}). The unused Typography import was dropped.
// The dropzone logic + onDrop prop are untouched.

// Arco's theme tokens rather than fixed hexes: the app flips to dark via
// body[arco-theme="dark"], and the old #fafafa/#eeeeee/#bdbdbd left a white
// slab glowing in the middle of a dark page.
const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 96,
    padding: '20px',
    borderWidth: 2,
    borderRadius: 4,
    borderColor: 'var(--color-border-3)',
    borderStyle: 'dashed',
    backgroundColor: 'var(--color-fill-1)',
    color: 'var(--color-text-3)',
    outline: 'none',
    cursor: 'pointer',
    transition: 'border-color .24s ease-in-out, background-color .24s ease-in-out'
};

// Drag states read as the primary/success/danger ramps, which are themed too.
const focusedStyle = {
    borderColor: 'rgb(var(--arcoblue-6))',
    backgroundColor: 'var(--color-fill-2)'
};

const acceptStyle = {
    borderColor: 'rgb(var(--green-6))',
    backgroundColor: 'var(--color-fill-2)'
};

const rejectStyle = {
    borderColor: 'rgb(var(--red-6))',
    backgroundColor: 'var(--color-fill-2)'
};

// Matches the server's ParseMultipartForm limit; the old 1MB cap here rejected
// files the API would have taken, silently, before they ever left the browser.
const maxSize = 10 * 1024 * 1024;

export default function StyledDropZone({onDrop}) {
    const {
        acceptedFiles,
        fileRejections,
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject
    } = useDropzone({
        // No `accept`: file assets are anything worth linking to from an email
        // or landing page, not just images. (The option that used to sit here
        // was a comma-joined string, which react-dropzone v14 ignores, so this
        // is what the dropzone has actually been doing.)
        onDrop,
        minSize: 0,
        maxSize,
    });

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
    }), [
        isFocused,
        isDragAccept,
        isDragReject
    ]);

    return (
        <Row gutter={16}>
            <Col span={24}>
                <div className="container">
                    <div {...getRootProps({style})}>
                        <input {...getInputProps()} />
                        <p>Drag 'n' drop some files here, or click to select files</p>
                    </div>
                </div>
            </Col>
            <Col span={24}>
                {fileRejections.length > 0 && (
                    <Alert
                        type="warning"
                        style={{marginTop: 8}}
                        title="Some files weren't uploaded"
                        content={
                            <ul style={{margin: 0, paddingLeft: 18}}>
                                {fileRejections.map(({file, errors}) => (
                                    <li key={file.name}>
                                        {file.name} — {errors.map((e) => (
                                            e.code === 'file-too-large'
                                                ? 'larger than the 10MB limit'
                                                : e.message
                                        )).join(', ')}
                                    </li>
                                ))}
                            </ul>
                        }
                    />
                )}
                {acceptedFiles.length > 0 && (
                    <>
                        <h5 style={{color: 'var(--color-text-2)'}}>Recently Uploaded File[s]</h5>
                        <Space wrap style={{marginBottom: 8}}>
                            {acceptedFiles.map(acceptedFile => (
                                <Tag key={acceptedFile.name} color="green">{acceptedFile.name}</Tag>
                            ))}
                        </Space>
                    </>
                )}
            </Col>
        </Row>
    );
}
